<template>
    <div class="bg-base-100 shadow w-72">
        <div v-if="!open" class="px-4 py-2 h-32">
            <div class="text-lg font-bold pb-2">Lås periode</div>
            <div tabindex="0"/>
            <div class="flex">
                <month-picker
                    v-model="startPeriodToBeClosed"
                    class="mr-2 w-24"
                    :label="includeEndPeriodPicker? 'Fra': 'Periode'"
                    size="sm"
                />
                <UButton
                    v-if="!includeEndPeriodPicker"
                    size="sm"
                    color="neutral"
                    variant="outline"
                    @click="toggleEndPeriodPicker"
                >
                    <div class="text-xl i-ph-plus"/>
                </UButton>
                <div v-if="includeEndPeriodPicker" class="flex items-center">
                    <month-picker
                        v-model="endPeriodToBeClosed"
                        class="w-24"
                        label="Til"
                        size="sm"
                    />
                    <UButton
                        class="ml-2"
                        size="sm"
                        color="neutral"
                        variant="outline"
                        @click="toggleEndPeriodPicker"
                    >
                        <div class="text-xl text-red-500 i-ph-x"/>
                    </UButton>
                </div>
            </div>
            <div v-if="startIsCurrentOrFuture && !startMustBeBeforeEnd" class="text-xs text-red-500 pt-2">Nåværende eller fremtidig perioder kan ikke låses</div>
            <div v-if="endIsCurrentOrFuture && !startIsCurrentOrFuture" class="text-xs pt-2">Nåværende eller fremtidig perioder vil ikke bli låst</div>
            <div v-if="startMustBeBeforeEnd" class="text-xs text-red-500 pt-2">Startmåned må være før sluttmåned</div>
        </div>
        <div v-else class="bg-base-100 px-4 py-2 shadow w-72">
            <div class="text-lg font-bold mb-6">Åpne periode {{ periodToOpen }}</div>
            <div class="flex flex-col justify-between items-end">
                <span class="justify-start self-start">Kommentar</span>
                <UTextarea v-model="comment" autoresize class="w-full"/>
            </div>
        </div>
        <div class="flex justify-between">
            <div class="grow"/>
            <UButton
                size="sm"
                variant="outline"
                color="neutral"
                class=" ml-4 mb-2 mr-2 w-24 justify-center text-sm"
                @click="onCloseDialog()"
            >
                Avbryt
            </UButton>
            <div>
                <UButton
                    v-if="!open"
                    size="sm"
                    color="primary"
                    class="mr-4 mb-2 w-24 text-sm justify-center"
                    :disabled="startIsCurrentOrFuture"
                    @click="lockPeriod"
                >
                    Lås
                </UButton>
                <UButton
                    v-else
                    :disabled="!comment || comment.length === 0"
                    size="sm"
                    color="primary"
                    class="mr-4 mb-2 w-24 text-sm justify-center"
                    @click="unlockPeriod"
                >
                    Åpne
                </UButton>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import hotkeys from 'hotkeys-js'
import MonthPicker from '@/dateUtils/MonthPicker.vue'
import {computed, ref} from 'vue'
import {closePeriod, closePeriods, MonthYear, openPeriod, Period} from '@/period-lock/periodLock.service'
import {notifyError} from '@/services/infrastructure/notification.service'

hotkeys('esc',  () => onCloseDialog())

const props = defineProps(
    {
        period: {type: Object as () => Period, required: false},
        open: {type: Boolean, required: true},
        close: {
            type: Function,
            required: true
        }
    }
)

const onCloseDialog = () => {
    hotkeys.unbind('esc')
    props.close()
}

const comment = ref('')
const includeEndPeriodPicker = ref(false)
const periodToOpen = computed(() => {
    if (!props.period) return ''
    const date = new Date(props.period.year, props.period.month - 1)
    return date.toLocaleString('no', {month: 'long', year: 'numeric'})
})

const startPeriodToBeClosed = ref<MonthYear>({
    month: props.period ? props.period.month - 1 : new Date().getMonth(),
    year: props.period ? props.period.year : new Date().getFullYear()
})

const endPeriodToBeClosed = ref<MonthYear>({
    month: props.period ? props.period.month - 1 : new Date().getMonth(),
    year: props.period ? props.period.year : new Date().getFullYear()
})

const startMustBeBeforeEnd = computed(() => {
    if (!includeEndPeriodPicker.value) return false
    const startPeriod = new Date(startPeriodToBeClosed.value.year, startPeriodToBeClosed.value.month)
    const endPeriod = new Date(endPeriodToBeClosed.value.year, endPeriodToBeClosed.value.month)
    return startPeriod > endPeriod
})

const startIsCurrentOrFuture = computed(() => {
    if (startPeriodToBeClosed.value.year > new Date().getFullYear()) return true
    return startPeriodToBeClosed.value.year === new Date().getFullYear() && startPeriodToBeClosed.value.month >= new Date().getMonth()
})

const endIsCurrentOrFuture = computed(() => {
    if (!includeEndPeriodPicker.value) return false
    if (endPeriodToBeClosed.value.year > new Date().getFullYear()) return true
    return endPeriodToBeClosed.value.year === new Date().getFullYear() && endPeriodToBeClosed.value.month >= new Date().getMonth()
})

const toggleEndPeriodPicker = () => {
    includeEndPeriodPicker.value = !includeEndPeriodPicker.value
}

const lockPeriod = async () => {
    try {
        if (includeEndPeriodPicker.value) {
            await closePeriods(startPeriodToBeClosed.value, endPeriodToBeClosed.value)
        } else {
            await closePeriod(startPeriodToBeClosed.value)
        }
    } catch (e: any) {
        notifyError({title: 'Feilet', description: `Låsing av periode feilet.\n${e.response.data.detail}:\n ${e.response.data.info.reasons.join(' \n')}`})
        onCloseDialog()
    }
    emit('period_closed')
    onCloseDialog()
}

const unlockPeriod = async () => {
    if (!comment.value || comment.value.length === 0 || !props.period) return
    await openPeriod(props.period.uuid, comment.value)
    emit('period_opened')
    onCloseDialog()
}

const emit = defineEmits(['period_closed', 'period_opened'])

</script>
<style scoped>

</style>